import { Controller } from '@hotwired/stimulus'
import { computePosition, flip, shift, offset, autoUpdate } from '@floating-ui/dom'
import tippy from 'tippy.js'

export default class extends Controller {
  static targets = ['trigger', 'dropdown', 'searchInput', 'results', 'loadingIndicator']
  static values = {
    currentCompanyId: Number,
    currentSection: String,
    url: String,
    loaded: { type: Boolean, default: false }
  }

  connect() {
    document.addEventListener('click', this.handleOutsideClick)
    document.addEventListener('keydown', this.handleKeydown)
    this.cleanup = null
    this.debounceTimer = null
    this.selectedIndex = -1
    this.allOffset = 0
    this.allHasMore = false
    this.isLoadingMore = false
  }

  disconnect() {
    document.removeEventListener('click', this.handleOutsideClick)
    document.removeEventListener('keydown', this.handleKeydown)
    clearTimeout(this.debounceTimer)
    this.debounceTimer = null
    this.cleanupAutoUpdate()
  }

  cleanupAutoUpdate() {
    if (this.cleanup) {
      this.cleanup()
      this.cleanup = null
    }
  }

  toggle = (event) => {
    event.preventDefault()
    event.stopPropagation()

    if (this.dropdownTarget.classList.contains('hidden')) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    this.dropdownTarget.classList.remove('hidden')
    this.triggerTarget.classList.add('active')

    // Position the dropdown
    this.cleanup = autoUpdate(this.triggerTarget, this.dropdownTarget, async () => {
      const { x, y } = await computePosition(this.triggerTarget, this.dropdownTarget, {
        placement: 'bottom-start',
        middleware: [offset(4), flip(), shift({ padding: 8 })],
        strategy: 'fixed'
      })

      Object.assign(this.dropdownTarget.style, {
        left: `${x}px`,
        top: `${y}px`,
        position: 'fixed'
      })
    })

    // Load companies if not already loaded
    if (!this.loadedValue) {
      this.loadCompanies()
    }

    // Add scroll listener for infinite scroll
    this.resultsTarget.addEventListener('scroll', this.handleScroll)

    // Auto-focus search input
    setTimeout(() => {
      if (this.hasSearchInputTarget) {
        this.searchInputTarget.focus()
      }
    }, 50)
  }

  close = () => {
    this.dropdownTarget.classList.add('hidden')
    this.triggerTarget.classList.remove('active')
    this.cleanupAutoUpdate()
    this.selectedIndex = -1

    // Remove scroll listener
    this.resultsTarget.removeEventListener('scroll', this.handleScroll)

    // Clear search
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ''
    }
  }

  handleScroll = () => {
    if (this.isLoadingMore || !this.allHasMore) return

    const { scrollTop, scrollHeight, clientHeight } = this.resultsTarget
    
    // Load more when user is near the bottom (within 100px)
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      this.loadMoreAll()
    }
  }

  async loadMoreAll() {
    if (this.isLoadingMore || !this.allHasMore) return

    this.isLoadingMore = true

    try {
      const params = new URLSearchParams({
        company_id: this.currentCompanyIdValue || '',
        load_more_all: 'true',
        offset: this.allOffset
      })

      const response = await fetch(`${this.urlValue}?${params}`)
      if (!response.ok) throw new Error('Failed to load more companies')

      const data = await response.json()

      this.appendAllCompanies(data.companies)
      this.allHasMore = data.has_more
      this.allOffset = data.next_offset
    } catch (error) {
      console.error('Failed to load more companies:', error)
    } finally {
      this.isLoadingMore = false
    }
  }

  appendAllCompanies(companies) {
    const allSection = this.resultsTarget.querySelector('.company-switcher__section:last-child')
    if (!allSection) return

    companies.forEach(company => {
      const html = `
        <a href="#" 
           class="company-switcher__item"
           data-company-id="${company.id}"
           data-company-slug="${company.slug}"
           data-action="click->company-switcher#selectCompany">
          <span class="company-switcher__item-name" data-full-name="${this.escapeHtml(company.name)}">
            ${this.escapeHtml(company.name)}
          </span>
        </a>
      `
      allSection.insertAdjacentHTML('beforeend', html)
    })

    // Add tooltips for new items
    requestAnimationFrame(() => this.addTooltipsForOverflowingText())
  }

  handleOutsideClick = (event) => {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  handleKeydown = (event) => {
    if (this.dropdownTarget.classList.contains('hidden')) return

    switch (event.key) {
      case 'Escape':
        this.close()
        this.triggerTarget.focus()
        break
      case 'ArrowDown':
        event.preventDefault()
        this.moveSelection(1)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.moveSelection(-1)
        break
      case 'Enter':
        event.preventDefault()
        this.selectCurrentItem()
        break
    }
  }

  moveSelection(delta) {
    const items = this.resultsTarget.querySelectorAll('.company-switcher__item:not(.company-switcher__item--current)')
    if (items.length === 0) return

    // Remove previous selection
    items.forEach(item => item.classList.remove('company-switcher__item--focused'))

    // Update index
    this.selectedIndex += delta
    if (this.selectedIndex < 0) this.selectedIndex = items.length - 1
    if (this.selectedIndex >= items.length) this.selectedIndex = 0

    // Add selection to new item
    const selectedItem = items[this.selectedIndex]
    if (selectedItem) {
      selectedItem.classList.add('company-switcher__item--focused')
      selectedItem.scrollIntoView({ block: 'nearest' })
    }
  }

  selectCurrentItem() {
    const items = this.resultsTarget.querySelectorAll('.company-switcher__item:not(.company-switcher__item--current)')
    
    // First, check if we have an arrow-key selected item
    if (this.selectedIndex >= 0 && this.selectedIndex < items.length) {
      items[this.selectedIndex].click()
      return
    }
    
    // Otherwise, check if a company item is currently focused (e.g., via Tab navigation)
    const focusedItem = document.activeElement
    if (focusedItem && focusedItem.classList.contains('company-switcher__item')) {
      focusedItem.click()
    }
  }

  search = () => {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => {
      this.loadCompanies(this.searchInputTarget.value)
    }, 200)
  }

  async loadCompanies(query = '') {
    this.showLoading()

    try {
      const params = new URLSearchParams({
        company_id: this.currentCompanyIdValue || '',
        q: query
      })

      const response = await fetch(`${this.urlValue}?${params}`)

      if (!response.ok) {
        throw new Error(`Failed to load companies: ${response.status} ${response.statusText}`)
      }
      
      const data = await response.json()

      this.renderResults(data, query)
      this.loadedValue = query === '' // Only mark as loaded for initial load
    } catch (error) {
      console.error('Failed to load companies:', error)
      this.resultsTarget.innerHTML = '<div class="company-switcher__error">Failed to load companies</div>'
    } finally {
      this.hideLoading()
    }
  }

  showLoading() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove('hidden')
    }
  }

  hideLoading() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add('hidden')
    }
  }

  renderResults(data, query) {
    this.selectedIndex = -1
    let html = ''

    if (data.search_results) {
      // Search mode - flat list (no infinite scroll)
      html = this.renderSection('', data.search_results, data.current_company_id)
      this.allHasMore = false
    } else {
      // Grouped mode
      if (data.family && data.family.length > 0) {
        html += this.renderSection('', data.family, data.current_company_id) // Family has no label per spec
      }
      if (data.favorites && data.favorites.length > 0) {
        html += this.renderSection('FAVORITES', data.favorites, data.current_company_id)
      }
      if (data.recents && data.recents.length > 0) {
        html += this.renderSection('RECENTS', data.recents, data.current_company_id)
      }
      if (data.all && data.all.length > 0) {
        html += this.renderSection('ALL', data.all, data.current_company_id)
      }

      // Store pagination info for infinite scroll
      this.allHasMore = data.all_has_more || false
      this.allOffset = data.all_offset || 0
    }

    if (html === '') {
      html = '<div class="company-switcher__empty">No companies found</div>'
    }

    this.resultsTarget.innerHTML = html
    
    // Add tooltips for overflowing text after DOM is updated
    requestAnimationFrame(() => this.addTooltipsForOverflowingText())
  }

  renderSection(title, companies, currentCompanyId) {
    if (!companies || companies.length === 0) return ''

    let html = '<div class="company-switcher__section">'

    if (title) {
      html += `<div class="company-switcher__section-title">${this.escapeHtml(title)}</div>`
    }

    companies.forEach(company => {
      const isCurrent = company.id === currentCompanyId || company.is_current
      const indentClass = company.indent ? 'company-switcher__item--indent' : ''
      const currentClass = isCurrent ? 'company-switcher__item--current' : ''

      html += `
        <a href="#" 
           class="company-switcher__item ${indentClass} ${currentClass}"
           data-company-id="${company.id}"
           data-company-slug="${company.slug}"
           data-action="click->company-switcher#selectCompany">
          <span class="company-switcher__item-name" data-full-name="${this.escapeHtml(company.name)}">
            ${this.escapeHtml(company.name)}
          </span>
          ${isCurrent ? '<i class="fas fa-check company-switcher__item-check"></i>' : ''}
        </a>
      `
    })

    html += '</div>'
    return html
  }

  selectCompany = (event) => {
    event.preventDefault()

    const companyId = event.currentTarget.dataset.companyId
    const companySlug = event.currentTarget.dataset.companySlug

    // Don't navigate if clicking current company
    if (parseInt(companyId) === this.currentCompanyIdValue) {
      this.close()
      return
    }

    // Build the navigation URL based on current section
    const url = this.buildNavigationUrl(companySlug)
    
    // Navigate using Turbo if available, otherwise regular navigation
    if (window.Turbo) {
      window.Turbo.visit(url)
    } else {
      window.location.href = url
    }

    this.close()
  }

  buildNavigationUrl(companySlug) {
    const section = this.currentSectionValue

    // Map section identifiers to URL patterns (company routes use /c/ prefix)
    const sectionMappings = {
      'home': `/c/${companySlug}`,
      'passwords': `/c/${companySlug}/passwords`,
      'procedures': `/c/${companySlug}/processes`,
      'articles': `/kba?company_id=${companySlug}`,
      'photos': `/c/${companySlug}/photos`,
      'networks': `/c/${companySlug}/networks`,
      'ip_addresses': `/c/${companySlug}/networks`,
      'vlans': `/c/${companySlug}/vlans`,
      'vlan_zones': `/c/${companySlug}/vlan_zones`,
      'network_maps': `/c/${companySlug}/network_map`,
      'rack_storages': `/c/${companySlug}/rack_storages`,
      'websites': `/c/${companySlug}/websites`,
      'expirations': `/expirations?company_id=${companySlug}`,
      'museum': `/c/${companySlug}/museum`,
      'portals': `/c/${companySlug}/portals`,
    }

    // Check if we have an asset_layout in the section (e.g., "assets:workstations")
    if (section && section.startsWith('assets:')) {
      const layoutSlug = section.split(':')[1]
      return `/c/${companySlug}/${layoutSlug}`
    }

    let url = sectionMappings[section] || `/c/${companySlug}`

    // Preserve tab query param from current URL (e.g., ?tab=related)
    const currentTab = new URLSearchParams(window.location.search).get('tab')
    if (currentTab && section === 'home') {
      url += `?tab=${currentTab}`
    }

    return url
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  addTooltipsForOverflowingText() {
    const nameElements = this.resultsTarget.querySelectorAll('.company-switcher__item-name')
    
    nameElements.forEach(el => {
      // Destroy existing tippy instance if any
      if (el._tippy) {
        el._tippy.destroy()
      }
      
      // Only add tooltip if text is overflowing
      if (el.scrollWidth > el.clientWidth) {
        const fullName = el.dataset.fullName
        if (fullName) {
          tippy(el, { content: fullName })
        }
      }
    })
  }
}

